/**
 * AddBalanceModal - Modal for manual wallet top-ups
 * Allows users to add funds to their wallet balance via saved payment methods
 * Uses Stripe PaymentIntent for secure off-session charging
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Wallet, CreditCard, Loader2, AlertCircle, ChevronDown } from 'lucide-react';
import { AddCardForm } from './AddCardForm';

interface PaymentMethod {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
  is_primary_subscription: boolean;
  is_primary_wallet: boolean;
}

interface AddBalanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  currentBalance: number;
  onBalanceAdded: (newBalance: number) => void;
  isInternal?: boolean;
}

// Preset amount options
const PRESET_AMOUNTS = [10, 25, 50, 100, 250, 500];
const MIN_AMOUNT = 10;
const MAX_AMOUNT = 1000;

export function AddBalanceModal({
  isOpen,
  onClose,
  locationId,
  currentBalance,
  onBalanceAdded,
  isInternal = false,
}: AddBalanceModalProps) {
  // Amount selection state
  const [selectedAmount, setSelectedAmount] = useState<number | 'other'>(50);
  const [customAmount, setCustomAmount] = useState('');

  // Payment method state
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string | 'add-new'>('');
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [needsCustomerSetup, setNeedsCustomerSetup] = useState(false);

  // Processing state
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isSubmittingRef = useRef(false); // Synchronous guard against double-submission

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Get the effective amount value
  const getEffectiveAmount = (): number => {
    if (selectedAmount === 'other') {
      const parsed = parseFloat(customAmount);
      return isNaN(parsed) ? 0 : parsed;
    }
    return selectedAmount;
  };

  const effectiveAmount = getEffectiveAmount();
  const projectedBalance = currentBalance + effectiveAmount;
  const isValidAmount = effectiveAmount >= MIN_AMOUNT && effectiveAmount <= MAX_AMOUNT;

  // Fetch payment methods
  const fetchPaymentMethods = useCallback(async () => {
    setLoadingPaymentMethods(true);
    setNeedsCustomerSetup(false);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/payment-methods`,
        { credentials: 'include' }
      );
      const data = await response.json() as {
        success: boolean;
        payment_methods?: PaymentMethod[];
        error?: string;
      };

      if (data.success && data.payment_methods) {
        setPaymentMethods(data.payment_methods);
        // Auto-select primary wallet card or first card
        const primaryWallet = data.payment_methods.find(pm => pm.is_primary_wallet);
        const firstCard = data.payment_methods[0];
        if (primaryWallet) {
          setSelectedPaymentMethodId(primaryWallet.id);
        } else if (firstCard) {
          setSelectedPaymentMethodId(firstCard.id);
        }
      } else if (data.error?.includes('No Stripe customer')) {
        // No Stripe customer - need to set up billing account first
        setNeedsCustomerSetup(true);
        setShowAddCard(true); // Auto-show AddCardForm which will display BusinessInfoForm
        setSelectedPaymentMethodId('add-new');
      }
    } catch (err) {
      console.error('[AddBalanceModal] Error fetching payment methods:', err);
    } finally {
      setLoadingPaymentMethods(false);
    }
  }, [locationId]);

  // Fetch payment methods when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPaymentMethods();
      // Reset state
      setSelectedAmount(50);
      setCustomAmount('');
      setShowAddCard(false);
      setError(null);
    }
  }, [isOpen, fetchPaymentMethods]);

  // Handle payment method dropdown change
  const handlePaymentMethodChange = (value: string) => {
    if (value === 'add-new') {
      setShowAddCard(true);
      setSelectedPaymentMethodId('add-new');
    } else {
      setShowAddCard(false);
      setSelectedPaymentMethodId(value);
    }
  };

  // Handle card added from AddCardForm
  const handleCardAdded = (newCard: PaymentMethod) => {
    setPaymentMethods(prev => [...prev, newCard]);
    setSelectedPaymentMethodId(newCard.id);
    setShowAddCard(false);
    setNeedsCustomerSetup(false); // Customer was created during card addition
  };

  // Clamp custom amount on blur
  const handleCustomAmountBlur = () => {
    const value = parseFloat(customAmount);
    if (!isNaN(value)) {
      if (value < MIN_AMOUNT) {
        setCustomAmount(MIN_AMOUNT.toString());
      } else if (value > MAX_AMOUNT) {
        setCustomAmount(MAX_AMOUNT.toString());
      }
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Synchronous guard against double-submission (state updates are async)
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    if (!isValidAmount) {
      setError(`Amount must be between ${formatCurrency(MIN_AMOUNT)} and ${formatCurrency(MAX_AMOUNT)}`);
      isSubmittingRef.current = false;
      return;
    }

    if (!selectedPaymentMethodId || selectedPaymentMethodId === 'add-new') {
      setError('Please select a payment method');
      isSubmittingRef.current = false;
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/add-balance`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            amount: effectiveAmount,
            payment_method_id: selectedPaymentMethodId,
          }),
        }
      );

      const data = await response.json() as {
        success: boolean;
        new_balance?: number;
        error?: string;
      };

      if (data.success && typeof data.new_balance === 'number') {
        // Success - notify parent with new balance and close
        onBalanceAdded(data.new_balance);
        onClose();
      } else {
        setError(data.error || 'Payment failed. Please try again.');
      }
    } catch (err) {
      console.error('[AddBalanceModal] Payment error:', err);
      setError('An error occurred while processing your payment. Please try again.');
    } finally {
      setProcessing(false);
      isSubmittingRef.current = false;
    }
  };

  // Get display info for a payment method
  const getPaymentMethodDisplay = (pm: PaymentMethod) => {
    const brandName = pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1);
    const primaryLabel = pm.is_primary_wallet ? ' (Primary)' : '';
    return `${brandName} ****${pm.last4}${primaryLabel}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Header - stays fixed at top */}
        <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Add Balance</h2>
          </div>
          <button
            onClick={onClose}
            disabled={processing}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
            {/* Internal User Note */}
            {isInternal && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-sm text-purple-800">
                  <span className="font-medium">Note:</span> As an internal user, adding balance is not required for processing. Your account bypasses billing requirements.
                </p>
              </div>
            )}

            {/* Balance Preview */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-green-700">Current Balance</span>
                <span className="text-sm font-medium text-green-800">
                  {formatCurrency(currentBalance)}
                </span>
              </div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-green-700">Adding</span>
                <span className="text-sm font-medium text-green-800">
                  + {formatCurrency(effectiveAmount)}
                </span>
              </div>
              <div className="border-t border-green-200 pt-2 mt-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-semibold text-green-700">New Balance</span>
                  <span className="text-lg font-bold text-green-800">
                    {formatCurrency(projectedBalance)}
                  </span>
                </div>
              </div>
            </div>

            {/* Amount Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount
              </label>
              <div className="relative">
                <select
                  value={selectedAmount}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedAmount(value === 'other' ? 'other' : parseInt(value));
                    if (value !== 'other') {
                      setCustomAmount('');
                    }
                  }}
                  disabled={processing}
                  className="block w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed appearance-none"
                >
                  {PRESET_AMOUNTS.map((amt) => (
                    <option key={amt} value={amt}>
                      {formatCurrency(amt)}
                    </option>
                  ))}
                  <option value="other">Other...</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              </div>

              {/* Custom Amount Input */}
              {selectedAmount === 'other' && (
                <div className="mt-2">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      onBlur={handleCustomAmountBlur}
                      placeholder="Enter amount"
                      min={MIN_AMOUNT}
                      max={MAX_AMOUNT}
                      disabled={processing}
                      className="block w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Min: {formatCurrency(MIN_AMOUNT)} | Max: {formatCurrency(MAX_AMOUNT)}
                  </p>
                </div>
              )}
            </div>

            {/* Payment Method Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Method
              </label>
              {loadingPaymentMethods ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : (
                <>
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2">
                      <CreditCard className="w-4 h-4 text-gray-400" />
                    </div>
                    <select
                      value={selectedPaymentMethodId}
                      onChange={(e) => handlePaymentMethodChange(e.target.value)}
                      disabled={processing}
                      className="block w-full pl-10 pr-8 py-2.5 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed appearance-none"
                    >
                      {paymentMethods.length === 0 && selectedPaymentMethodId !== 'add-new' && (
                        <option value="" disabled>No saved cards</option>
                      )}
                      {paymentMethods.map((pm) => (
                        <option key={pm.id} value={pm.id}>
                          {getPaymentMethodDisplay(pm)}
                        </option>
                      ))}
                      <option value="add-new">Add New Card...</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>

                  {/* Inline Add Card Form */}
                  {showAddCard && (
                    <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                      {needsCustomerSetup && (
                        <div className="mb-3 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md p-2">
                          Please set up your billing account before adding balance.
                        </div>
                      )}
                      <AddCardForm
                        locationId={locationId}
                        onSuccess={handleCardAdded}
                        onCancel={needsCustomerSetup ? onClose : () => {
                          setShowAddCard(false);
                          // Reselect the primary card or first available
                          const primaryWallet = paymentMethods.find(pm => pm.is_primary_wallet);
                          const firstCard = paymentMethods[0];
                          if (primaryWallet) {
                            setSelectedPaymentMethodId(primaryWallet.id);
                          } else if (firstCard) {
                            setSelectedPaymentMethodId(firstCard.id);
                          } else {
                            setSelectedPaymentMethodId('');
                          }
                        }}
                        setAsPrimarySubscription={false}
                        setAsPrimaryTopup={true}
                        showCancelButton={!needsCustomerSetup}
                      />
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start p-3 bg-red-50 border border-red-200 rounded-md">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
        </div>

        {/* Footer - stays fixed at bottom */}
        <div className="flex-shrink-0 flex gap-3 p-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={processing}
              className="flex-1 py-2.5 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={processing || !isValidAmount || !selectedPaymentMethodId || selectedPaymentMethodId === 'add-new'}
              className={`flex-1 py-2.5 px-4 rounded-md text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center ${
                isValidAmount && selectedPaymentMethodId && selectedPaymentMethodId !== 'add-new'
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                `Add ${formatCurrency(effectiveAmount)}`
              )}
            </button>
        </div>
      </div>
    </div>
  );
}
